import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Hostel } from '@/api/types';
import { X, ScrollText, Building2 } from 'lucide-react';

type TFunc = ReturnType<typeof useTranslation>['t'];

export function getPlatformTerms(t: TFunc): string[] {
  return [
    t('tenant.tc1', 'You agree to comply with all hostel rules and regulations set by the hostel management.'),
    t('tenant.tc2', "Rent is due by the date specified each billing month. Late payments may attract penalties at the hostel's discretion."),
    t('tenant.tc3', 'The hostel management may terminate your stay with reasonable notice for breach of rules or non-payment.'),
    t('tenant.tc4', 'You are responsible for the safety and maintenance of hostel property assigned to you.'),
    t('tenant.tc5', 'Personal belongings are your own responsibility. The hostel is not liable for loss or damage.'),
    t('tenant.tc6', 'The management reserves the right to update these terms with prior notice.'),
  ];
}

// ── T&C modal ──────────────────────────────────────────────────────────────
export function TcModal({ hostel, onClose }: { hostel: Hostel | null; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-chrome-page rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <ScrollText size={18} className="text-amber-600" /> {t('tenant.termsAndConditions', 'Terms & Conditions')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-5">
          {hostel && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Building2 size={12} className="text-amber-600" /> {t('tenant.hostelRulesRegulations', '{{name}} — Hostel Rules & Regulations', { name: hostel.name })}
              </p>
              {hostel.rules ? (
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed bg-amber-50 border border-amber-100 rounded-xl p-4">{hostel.rules}</pre>
              ) : (
                <p className="text-sm text-gray-400 italic bg-gray-50 rounded-xl p-4">{t('tenant.noRulesSet', 'No specific rules have been set by this hostel yet.')}</p>
              )}
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('tenant.platformTermsTitle', 'Platform Terms & Conditions')}</p>
            <ul className="space-y-2.5">
              {getPlatformTerms(t).map((term, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />{term}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-gray-400 border-t border-gray-100 pt-4">
            {t('tenant.signingAcknowledgement', 'By signing on the tenant portal you acknowledge that you have read, understood, and agree to all of the above.')}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Signature pad canvas ─────────────────────────────────────────────────────
export function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing   = useRef(false);
  const lastPos   = useRef<{ x: number; y: number } | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect   = canvas.getBoundingClientRect();
    const sx     = canvas.width  / rect.width;
    const sy     = canvas.height / rect.height;
    if ('touches' in e) {
      const t = e.touches[0];
      return { x: (t.clientX - rect.left) * sx, y: (t.clientY - rect.top) * sy };
    }
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  };

  const startDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e);
  }, []);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!drawing.current || !lastPos.current) return;
    const canvas = canvasRef.current!;
    const ctx    = canvas.getContext('2d')!;
    const pos    = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.stroke();
    lastPos.current = pos;
    setIsEmpty(false);
    onChange(canvas.toDataURL('image/png'));
  }, [onChange]);

  const stopDraw = useCallback(() => {
    drawing.current = false;
    lastPos.current = null;
  }, []);

  const clear = () => {
    const canvas = canvasRef.current!;
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
    onChange(null);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-gray-600">{t('tenant.signature', 'Signature')} <span className="text-red-500">*</span></p>
        {!isEmpty && (
          <button type="button" onClick={clear} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 transition">
            <X size={11} /> {t('common.clear', 'Clear')}
          </button>
        )}
      </div>
      <div className={`relative rounded-lg border-2 bg-white overflow-hidden ${isEmpty ? 'border-dashed border-gray-300' : 'border-gray-400'}`}>
        <canvas
          ref={canvasRef}
          width={560}
          height={120}
          className="w-full h-28 touch-none cursor-crosshair block"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
        {isEmpty && (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-gray-300 pointer-events-none select-none">
            {t('tenant.signHere', 'Sign here')}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Owner/supervisor "sign on behalf" fields ────────────────────────────────
export interface SignOnBehalfValue {
  enabled: boolean;
  agreed: boolean;
  signature: string | null;
}

export const EMPTY_SIGN_ON_BEHALF: SignOnBehalfValue = { enabled: false, agreed: false, signature: null };

/**
 * Lets an owner/supervisor record that a tenant without a smartphone has agreed to the
 * terms in person, from the owner/supervisor's own screen. `forceOpen` skips the enable
 * checkbox for contexts where signing is already the sole purpose of the panel.
 */
export function SignOnBehalfFields({ hostel, value, onChange, forceOpen }: {
  hostel: Hostel | null;
  value: SignOnBehalfValue;
  onChange: (v: SignOnBehalfValue) => void;
  forceOpen?: boolean;
}) {
  const { t } = useTranslation();
  const [showTc, setShowTc] = useState(false);
  const open = forceOpen || value.enabled;

  return (
    <div className="space-y-3">
      {showTc && <TcModal hostel={hostel} onClose={() => setShowTc(false)} />}

      {!forceOpen && (
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={e => onChange({ ...value, enabled: e.target.checked })}
            className="mt-0.5 w-4 h-4 accent-amber-600 cursor-pointer shrink-0"
          />
          <span className="text-sm text-gray-700">
            {t('tenant.signOnBehalfCheckbox', 'This tenant does not have a smartphone — sign the rental agreement on their behalf now.')}
          </span>
        </label>
      )}

      {open && (
        <div className="border border-amber-200 bg-amber-50/40 rounded-xl p-4 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={value.agreed}
              onChange={e => onChange({ ...value, agreed: e.target.checked })}
              className="mt-0.5 w-4 h-4 accent-amber-600 cursor-pointer shrink-0"
            />
            <span className="text-sm text-gray-700">
              {t('tenant.signOnBehalfConfirm', 'I have read/explained the')}{' '}
              <button
                type="button"
                onClick={e => { e.preventDefault(); setShowTc(true); }}
                className="text-amber-600 underline underline-offset-2 hover:text-amber-800 font-medium"
              >
                {t('tenant.termsAndConditions', 'Terms & Conditions')}
              </button>
              {' '}{t('tenant.signOnBehalfConfirmSuffix', 'to the tenant, and they have agreed.')}
            </span>
          </label>

          <SignaturePad onChange={sig => onChange({ ...value, signature: sig })} />
        </div>
      )}
    </div>
  );
}
